import { Injectable } from "@angular/core";
import { HttpClient, HttpContext, HttpErrorResponse } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { ExtendedMatchData } from "../../services/feed.types";
import { BOUND_REPORT_CURRENCY } from '../../reporting/report-currency';
import { RiskConditionField } from './risk-currency';

export interface RiskAccountContext {
    currency?: string; currency_id?: number; wallet_id?: string; wallet_kind?: string;
}

export interface RiskRuleCondition {
    currency?: string;
    bool_value?: boolean;
    values?: string[];
    type: string;
    field?: string;
    operator?: string;
    value?: number | string;
    rules?: RiskRuleCondition[];
}

export interface RiskRuleAction {
    decision: string;
    reason: string;
}

export interface RiskRuleScope {
    level: string;
    sport_id?: string;
}

export interface RiskRule {
    id: string;
    account_id: string;
    name: string;
    enabled: boolean;
    scope: RiskRuleScope;
    conditions: RiskRuleCondition[];
    action: RiskRuleAction;
    version: number;
    created_at: string;
    updated_at: string;
}

export interface ManualVerificationTicket extends RiskAccountContext {
    id: string;
    ticket_id: number;
    ref_code: string;
    tenant_id: string;
    user_id: string;
    status: string;
    risk_decision: string;
    risk_reason: string;
    contains_live: boolean;
    is_single: boolean;
    created_at: string;
    updated_at: string;
}

export interface TriggeredRule {
    rule_id: string;
    rule_name: string;
    decision: string;
    reason: string;
}

export interface TicketSummary {
    bonus: number;
    stake: number;
    max_gain: number;
    min_gain: number;
    max_quota: number;
    min_quota: number;
    gain_with_bonus: number;
    total_combinations: number;
    stake_per_combination: number;
}

export interface FixSelection {
    odd: number;
    code_id: string;
    event_id: number;
    group_id: string;
    sport_id: string;
    match_name: string;
    market_code: string;
    away_team_id: string;
    home_team_id: string;
    competition_id: string;
    market_selection: string;
    band?: string;
    line?: string;
    result?: string;
    r3?: string;
    period?: string;
    frameNumber?: string;
}

export interface SystemEvent {
    odd: number;
    code_id: string;
    event_id: number;
    group_id: string;
    sport_id: string;
    match_name: string;
    market_code: string;
    away_team_id: string;
    home_team_id: string;
    competition_id: string;
    market_selection: string;
    band?: string;
    line?: string;
    result?: string;
    r3?: string;
    period?: string;
    frameNumber?: string;
}

export interface SystemSelection {
    type: string[];
    events: SystemEvent[];
}

export interface TicketSelections {
    fixes: FixSelection[];
    systems: SystemSelection[];
}

export interface OriginalTicket extends RiskAccountContext {
    summary: TicketSummary;
    user_id: string;
    selections: TicketSelections;
    wallet_type: string;
}

export interface SelectionChangeDetail {
    new_value?: { odd?: number };
    old_value?: { odd?: number };
    match_name: string;
    change_type: string;
    selection_key: {
        market_selection: string;
        market_code: string;
        event_id: number;
        band?: string;
        line?: string;
    };
}

export interface SummaryChanges {
    stake?: number;
}

export interface BoundValue {
    band: string;
    line: string;
    odd: number;
}

export interface BoundChangeDetail {
    selection_key: SelectionKey;
    new_selection_key: SelectionKey;
    change_type: string;
    match_name: string;
    old_value: BoundValue;
    new_value: BoundValue;
}

export interface TicketChanges {
    has_changes: boolean;
    change_types: string[];
    selection_changes: SelectionChangeDetail[];
    selection_bound_changes?: BoundChangeDetail[];
    summary_changes?: SummaryChanges;
}

export interface ManualVerificationTicketDetail extends ManualVerificationTicket {
    triggered_rules: TriggeredRule[];
    original_ticket: OriginalTicket;
    proposed_ticket?: OriginalTicket;
    changes?: TicketChanges;
    operator_decision?: string;
}

export interface PaginationMeta {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    Offset: number;
}

export interface PaginatedResponse<T> {
    data: T[];
    meta: PaginationMeta;
}

export interface TicketSelection {
    id: number;
    tenant_id?: string;
    ticket_id: number;
    event_id: number;
    code_id: string;
    match_name: string;
    market_code: string;
    market_selection: string;
    band: string;
    line: string;
    odds: number;
    is_fix: boolean;
    is_single: boolean;
    is_in_system: boolean;
    status?: string;
    added_by?: string;
    updated_by?: string;
    added_date?: string;
    updated_date?: string;
}

export interface TicketSystemSelection {
    id: number;
    tenant_id?: string;
    system_id: number;
    selection_id: number;
    event_id: number;
    marketID?: string;
    added_by?: string;
    updated_by?: string;
    added_date?: string;
    updated_date?: string;
}

export interface TicketSystem {
    id: number;
    tenant_id?: string;
    ticket_id: number;
    system_from: number;
    system_to: number;
    name: string;
    is_mandatory: boolean;
    selections: TicketSystemSelection[];
    added_by?: string;
    updated_by?: string;
    added_date?: string;
    updated_date?: string;
}

export interface Ticket extends RiskAccountContext {
    id: number;
    tenant_id?: string;
    ref_code: string;
    user_id: string;
    is_single: boolean;
    has_system: boolean;
    contains_live: boolean;
    nr_combinations: number;
    stake: number;
    wallet_type: string;
    wallet_id?: string;
    min_quota: number;
    max_quota: number;
    min_gain: number;
    max_gain: number;
    bonus_perc: number;
    gain_with_bonus: number;
    gain: number;
    hash_ticket: string;
    hash_events: string;
    hash_events_market_codes: string;
    hash_events_market_codes_market_selections: string;
    hash_events_market_codes_market_selections_odds: string;
    body_json?: OriginalTicket;
    status: string;
    time_placed: string;
    first_event_start_at?: string;
    last_event_start_at?: string;
    added_by?: string;
    updated_by?: string;
    added_date: string;
    updated_date: string;
    selections: TicketSelection[];
    systems: TicketSystem[];
}

export interface SelectionKey {
    band: string;
    event_id: number;
    line: string;
    market_code: string;
    market_selection: string;
}

export interface SelectionChange {
    new_odd: number;
    selection_key: SelectionKey;
}

export interface SelectionRemoval {
    selection_key: SelectionKey;
}

// Moves a selection's bound (band and/or line) together with its price.
// selection_key addresses the selection as it is today; new_band/new_line must
// both be sent -- "" for a market that has no line, never null.
export interface SelectionBoundChange {
    selection_key: SelectionKey;
    new_band: string;
    new_line: string;
    new_odd: number;
}

export interface ProposedChanges {
    selection_changes: SelectionChange[];
    selection_removals: SelectionRemoval[];
    selection_bound_changes?: SelectionBoundChange[];
    stake: number;
}

export interface ManualVerificationDecisionRequest {
    decision: 'ACCEPT' | 'REJECT' | 'PROPOSE_CHANGES';
    operator_id: string;
    operator_name: string;
    proposed_changes?: ProposedChanges;
    reason: string;
}

export interface EventStatus {
    odd: number | null;
    startTime: string | null;
    isLive: boolean;
    isMarketLocked: boolean;
}

export interface UserRisk {
    userId: string;
    customFlag: string;
    comment: string;
}

interface UserRiskApiDto {
    UserID: string;
    CustomFlag: string;
    Comment: string;
    userId?: string;
    customFlag?: string;
    comment?: string;
}

@Injectable({
    providedIn: "root",
})
export class RiskService {
    private riskServiceUrl = environment.riskServiceUrl;
    private ticketServiceUrl = environment.ticketServiceUrl;
    private crmServiceUrl = environment.crmServiceUrl;
    private bettingFeedUrl = environment.bettingFeedUrl;

    constructor(private http: HttpClient) { }

    getRulesForAccount(): Observable<RiskRule[]> {
        const url = `${this.riskServiceUrl}/rule`;
        return this.http.get<RiskRule[]>(url);
    }

    createRule(ruleData: any): Observable<RiskRule> {
        const url = `${this.riskServiceUrl}/rule`;
        return this.http.post<RiskRule>(url, ruleData);
    }

    getAvailableFields(): Observable<RiskConditionField[]> {
        const url = `${this.riskServiceUrl}/rule/condition/field`;
        return this.http.get<RiskConditionField[]>(url);
    }

    getTicketsForManualVerification(page: number = 1, per_page: number = 10): Observable<PaginatedResponse<ManualVerificationTicket>> {
        const url = `${this.ticketServiceUrl}/admin/manual-verifications?page=${page}&per_page=${per_page}&status=pending_operator,claimed`;
        return this.http.get<PaginatedResponse<ManualVerificationTicket>>(url);
    }

    getAllManualVerificationTickets(page: number = 1, per_page: number = 10): Observable<PaginatedResponse<ManualVerificationTicket>> {
        const url = `${this.ticketServiceUrl}/admin/manual-verifications?order=desc&page=${page}&per_page=${per_page}`;
        return this.http.get<PaginatedResponse<ManualVerificationTicket>>(url);
    }

    getTicketForManualVerification(ticketId: string): Observable<ManualVerificationTicketDetail> {
        const url = `${this.ticketServiceUrl}/admin/manual-verifications/${ticketId}`;
        return this.http.get<ManualVerificationTicketDetail>(url);
    }

    submitManualVerificationDecision(ticketId: string, decision: ManualVerificationDecisionRequest): Observable<any> {
        const url = `${this.ticketServiceUrl}/admin/manual-verifications/${ticketId}/decision`;
        return this.http.post<any>(url, decision);
    }

    getSimilarTickets(refCode: string): Observable<Ticket[]> {
        const url = `${this.ticketServiceUrl}/tickets/similar?ref_code=${encodeURIComponent(refCode)}`;
        return this.http.get<Ticket[]>(url);
    }

    getSameTickets(refCode: string): Observable<Ticket[]> {
        const url = `${this.ticketServiceUrl}/tickets/same?ref_code=${encodeURIComponent(refCode)}`;
        return this.http.get<Ticket[]>(url);
    }

    claimTicketForVerification(ticketId: string, operatorId: string, operatorName: string): Observable<any> {
        const url = `${this.ticketServiceUrl}/admin/manual-verifications/${ticketId}/claim`;
        const body = {
            operator_id: operatorId,
            operator_name: operatorName
        };
        return this.http.post<any>(url, body);
    }

    getMatchSnapshot(eventId: number): Observable<ExtendedMatchData | null> {
        const url = `${this.bettingFeedUrl}?matchId=${eventId}&parts=M,H,P,R,S`;
        return this.http.get<any[]>(url).pipe(
            map(response => {
                if (!response || response.length === 0) return null;
                const data = response[0];
                const header = Array.isArray(data.H) ? data.H[0] : data.H;
                if (!header) return null;
                const period = Array.isArray(data.P) ? data.P[0] : data.P;
                const result = Array.isArray(data.R) ? data.R[0] : data.R;
                return {
                    H: header,
                    M: Array.isArray(data.M) ? data.M : [],
                    P: period,
                    R: result,
                    S: Array.isArray(data.S) ? data.S : undefined,
                    openMarketCodesCount: 0,
                } as ExtendedMatchData;
            }),
            catchError(error => {
                console.error(`Error fetching match snapshot for event ${eventId}:`, error);
                return of(null);
            })
        );
    }

    getPlayer360Data(userId: string, currency: string, days: number = 360): Observable<any> {
        const url = `${this.crmServiceUrl}/crm/players/${encodeURIComponent(userId)}/360`;
        return this.http.get<any>(url, {params: {days, currency}, context: new HttpContext().set(BOUND_REPORT_CURRENCY, currency)});
    }

    enableAutoRejectSame(ticketId: string, userId: string): Observable<any> {
        const url = `${this.riskServiceUrl}/auto-reject`;
        const body = {
            ticketId: ticketId,
            userId: userId
        };
        return this.http.post<any>(url, body);
    }

    disableAutoRejectSame(ticketId: string, userId: string): Observable<any> {
        const url = `${this.riskServiceUrl}/auto-reject/remove`;
        const body = {
            ticketId: ticketId,
            userId: userId
        };
        return this.http.post<any>(url, body);
    }

    getAutoRejectByRefCode(refCode: string): Observable<{ ticketId: string; found: boolean }> {
        const url = `${this.riskServiceUrl}/auto-reject?ticketID=${encodeURIComponent(refCode)}`;
        return this.http.get<{ ticketId: string; found: boolean }>(url);
    }

    checkAutoRejectSame(ticketIds: string[], userId: string): Observable<{ isRejected: boolean }> {
        const url = `${this.riskServiceUrl}/auto-reject/check`;
        const body = {
            ticketIds: ticketIds,
            userId: userId
        };
        return this.http.post<{ isRejected: boolean }>(url, body);
    }

    getUserRisk(userId: string): Observable<UserRisk> {
        const url = `${this.riskServiceUrl}/user-risk/${userId}`;
        return this.http.get<UserRiskApiDto>(url).pipe(
            map((response) => ({
                userId: response?.UserID || response?.userId || userId,
                customFlag: response?.CustomFlag || response?.customFlag || '',
                comment: response?.Comment || response?.comment || ''
            })),
            catchError((error: HttpErrorResponse) => {
                // 404 is expected when user has no custom risk data - not an error
                if (error.status === 404) {
                    return of({ userId: userId, customFlag: '', comment: '' });
                }
                // Log other errors
                console.error('Error fetching user risk:', error);
                return of({ userId: userId, customFlag: '', comment: '' });
            })
        );
    }

    updateUserRisk(userRisk: UserRisk): Observable<UserRisk> {
        const url = `${this.riskServiceUrl}/user-risk`;
        const body = {
            UserID: userRisk.userId,
            CustomFlag: userRisk.customFlag || '',
            Comment: userRisk.comment || ''
        };
        return this.http.post<UserRiskApiDto>(url, body).pipe(
            map((response) => ({
                userId: response?.UserID || response?.userId || userRisk.userId,
                customFlag: response?.CustomFlag || response?.customFlag || '',
                comment: response?.Comment || response?.comment || ''
            }))
        );
    }

    deleteRule(ruleId: string): Observable<any> {
        const url = `${this.riskServiceUrl}/rule/${ruleId}`;
        return this.http.delete<any>(url);
    }
}
